import CheckIcon from '@material-ui/icons/Check'
import EmailIcon from '@material-ui/icons/Email'
import PersonIcon from '@material-ui/icons/Person'
import type { ResultsTableProps, TableCellProps } from '@paintscout/ui'
import { ActionButton, ResultsTable, TableCell, TableRow, Typography, Link } from '@paintscout/ui'
import type { Auth0User } from 'paintscout'
import React, { useState } from 'react'
import { getHighestRoleName } from '@paintscout/util'
import moment from 'moment'
import { Card, makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core'
import { useSnackbar } from 'notistack'

export interface UserSearchTableProps extends Omit<ResultsTableProps, 'rows' | 'columns' | 'page' | 'renderRow'> {
  users: any[]
  onFetchNextPage?: (nextPage: number) => any
}
const useStyles = makeStyles<Theme>((_theme) => ({
  root: {},
  card: {
    overflowX: 'auto'
  }
}))

const copyActions = [
  { key: 'copyName', label: 'Copy Name', icon: PersonIcon },
  { key: 'copyEmail', label: 'Copy Email', icon: EmailIcon }
]

export default function UserSearchTable({ users, onFetchNextPage, onPageChange, ...other }: UserSearchTableProps) {
  const [page, setPage] = useState(1)
  const classes = useStyles({})
  const { enqueueSnackbar } = useSnackbar()

  // checkbox/actions are disabled until we figure out how we want to handle "deleting" users (deactivating)
  return (
    <Card className={classes.card} data-testid={'quote-list'}>
      <ResultsTable
        rows={users || []}
        noResultsMessage="No results"
        columns={
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Role</TableCell>
            <TableCell>Last Login</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Active</TableCell>
            <TableCell />
          </TableRow>
        }
        renderRow={(row) => {
          const role = getHighestRoleName(row.app_metadata.roles)
          const lastLogin = row.last_login ? moment(row.last_login).format('YYYY-MM-DD') : ''
          const createdDate = row.created ? moment(row.created).format('YYYY-MM-DD') : ''

          return (
            <TableRow key={row.user_id}>
              <TableCellLink row={row} disabled={role === 'Superadmin'}>
                <Typography variant={'body1'}>
                  {row.user_metadata.firstName} {row.user_metadata.lastName}
                </Typography>
                <Typography variant={'subtitle2'}>{row.email}</Typography>
              </TableCellLink>
              <TableCellLink row={row} style={{ textTransform: 'capitalize' }}>
                {role}
              </TableCellLink>
              <TableCellLink row={row}>{lastLogin}</TableCellLink>
              <TableCellLink row={row}>{createdDate}</TableCellLink>
              <TableCellLink row={row}>{row.app_metadata.active ? <CheckIcon /> : null}</TableCellLink>
              <TableCell align="right">
                <ActionButton
                  actions={copyActions}
                  onActionClick={(_ev, action) => {
                    if (action === 'copyName') {
                      navigator.clipboard.writeText(
                        [row.user_metadata.firstName, row.user_metadata.lastName].filter(Boolean).join(' ')
                      )
                      enqueueSnackbar('User name copied to clipboard.', { variant: 'success' })
                    } else if (action === 'copyEmail') {
                      navigator.clipboard.writeText(row.email ?? '')
                      enqueueSnackbar('User email copied to clipboard.', { variant: 'success' })
                    }
                  }}
                />
              </TableCell>
            </TableRow>
          )
        }}
        onPageChange={(nextPage) => {
          if (onFetchNextPage && nextPage > page && users.length < other.rowsPerPage * nextPage) {
            onFetchNextPage(nextPage)
          }

          if (onPageChange) {
            onPageChange(nextPage)
          }

          setPage(nextPage)
        }}
        {...other}
      />
    </Card>
  )
}

function TableCellLink({ row, disabled, ...other }: { row: Auth0User; disabled?: boolean } & TableCellProps) {
  const linkComponent = disabled
    ? null
    : (cProps: any) => <Link to={{ pathname: `/users/${encodeURIComponent(row.user_id)}` }} {...cProps} />
  return <TableCell component={linkComponent} {...other} />
}
